import React, { useState } from "react";
import { post } from "../../../services/smartApiService";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

interface FinancialDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: number;
  companyName: string;
  companySymbol: string;
}

interface FinancialDataFormData {
  // Required metadata
  quarter: string;
  year: number;
  quarter_end_date: string;
  financial_year: string;

  // Key metrics
  eps: number | null;
  revenue: number | null;
  revenue_growth_yoy: number | null;
  revenue_growth_qoq: number | null;
  net_profit: number | null;
  net_profit_margin: number | null;

  // Profitability ratios
  roe: number | null;
  roce: number | null;
  roa: number | null;
  operating_profit_margin: number | null;
  gross_profit_margin: number | null;

  // Valuation ratios
  pe_ratio: number | null;
  pb_ratio: number | null;
  price_to_sales: number | null;
  ev_to_ebitda: number | null;

  // Balance sheet items (in crores)
  total_assets: number | null;
  total_liabilities: number | null;
  shareholders_equity: number | null;
  book_value_per_share: number | null;

  // Debt metrics
  debt_to_equity: number | null;
  debt_to_assets: number | null;
  interest_coverage: number | null;
  current_ratio: number | null;
  quick_ratio: number | null;

  // Cash flow
  operating_cash_flow: number | null;
  free_cash_flow: number | null;
  cash_flow_per_share: number | null;

  // Sector-specific
  order_book: number | null;

  // EPS metrics
  eps_growth: number | null;
  diluted_eps: number | null;

  // Other
  asset_turnover: number | null;
  data_source: string;
}

const schema = Yup.object({
  quarter: Yup.string().required("Quarter is required").oneOf(['Q1', 'Q2', 'Q3', 'Q4']),
  year: Yup.number().required("Year is required").min(2000).max(new Date().getFullYear() + 1),
  quarter_end_date: Yup.string().required("Quarter end date is required"),
  financial_year: Yup.string().required("Financial year is required"),

  // Optional numeric fields
  eps: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  revenue: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
  revenue_growth_yoy: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  revenue_growth_qoq: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  net_profit: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  net_profit_margin: Yup.number().nullable().min(0).max(100).transform((v, o) => o === '' ? null : v),

  roe: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  roce: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  roa: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  operating_profit_margin: Yup.number().nullable().min(0).max(100).transform((v, o) => o === '' ? null : v),
  gross_profit_margin: Yup.number().nullable().min(0).max(100).transform((v, o) => o === '' ? null : v),

  pe_ratio: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
  pb_ratio: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
  price_to_sales: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
  ev_to_ebitda: Yup.number().nullable().transform((v, o) => o === '' ? null : v),

  total_assets: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
  total_liabilities: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
  shareholders_equity: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  book_value_per_share: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),

  debt_to_equity: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
  debt_to_assets: Yup.number().nullable().min(0).max(1).transform((v, o) => o === '' ? null : v),
  interest_coverage: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  current_ratio: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
  quick_ratio: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),

  operating_cash_flow: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  free_cash_flow: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  cash_flow_per_share: Yup.number().nullable().transform((v, o) => o === '' ? null : v),

  order_book: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),

  eps_growth: Yup.number().nullable().transform((v, o) => o === '' ? null : v),
  diluted_eps: Yup.number().nullable().transform((v, o) => o === '' ? null : v),

  asset_turnover: Yup.number().nullable().min(0).transform((v, o) => o === '' ? null : v),
  data_source: Yup.string().required(),
});

const FinancialDataModal: React.FC<FinancialDataModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  companyName,
  companySymbol,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'profitability' | 'valuation' | 'balance' | 'debt' | 'cash'>('basic');

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);

  // Helper to render number input with null handling
  const renderNumberInput = (field: any, placeholder: string, step: string = "0.01") => (
    <input
      {...field}
      value={field.value ?? ''}
      onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      className="input"
      type="number"
      step={step}
      placeholder={placeholder}
    />
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FinancialDataFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      quarter: `Q${currentQuarter}`,
      year: currentYear,
      quarter_end_date: new Date().toISOString().split("T")[0],
      financial_year: `FY${currentYear}`,
      data_source: "Manual",

      eps: null,
      revenue: null,
      revenue_growth_yoy: null,
      revenue_growth_qoq: null,
      net_profit: null,
      net_profit_margin: null,

      roe: null,
      roce: null,
      roa: null,
      operating_profit_margin: null,
      gross_profit_margin: null,

      pe_ratio: null,
      pb_ratio: null,
      price_to_sales: null,
      ev_to_ebitda: null,

      total_assets: null,
      total_liabilities: null,
      shareholders_equity: null,
      book_value_per_share: null,

      debt_to_equity: null,
      debt_to_assets: null,
      interest_coverage: null,
      current_ratio: null,
      quick_ratio: null,

      operating_cash_flow: null,
      free_cash_flow: null,
      cash_flow_per_share: null,

      order_book: null,

      eps_growth: null,
      diluted_eps: null,

      asset_turnover: null,
    },
  });

  const onSubmit = (data: FinancialDataFormData) => {
    setLoading(true);
    setSubmitResult(null);

    post(`/api/companies/${companyId}/financials`, data).subscribe({
      next: (response: any) => {
        console.log("Financial data submitted successfully:", response);
        setSubmitResult(response.data);
        setLoading(false);

        // Show success for 2 seconds, then close
        setTimeout(() => {
          reset();
          setSubmitResult(null);
          onSuccess();
        }, 3000);
      },
      error: (err: any) => {
        console.error("Failed to submit financial data:", err);
        setLoading(false);
        alert(err?.error?.detail || "Failed to submit financial data");
      },
    });
  };

  const handleClose = () => {
    reset();
    setSubmitResult(null);
    setActiveTab('basic');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`modal ${isOpen ? "is-active" : ""}`}>
      <div className="modal-background" onClick={handleClose}></div>
      <div className="modal-card" style={{ width: "900px", maxWidth: "95vw" }}>
        <header className="modal-card-head">
          <p className="modal-card-title">
            <i className="fas fa-file-invoice-dollar mr-2"></i>
            Submit Financial Data - {companyName} ({companySymbol})
          </p>
          <button className="delete" aria-label="close" onClick={handleClose}></button>
        </header>

        <section className="modal-card-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {submitResult ? (
            <div className="notification is-success">
              <h4 className="title is-5">✓ Financial Data Submitted Successfully!</h4>
              <p className="mb-3">
                <strong>Quarter:</strong> {submitResult.quarter} {submitResult.year}
              </p>

              <div className="box has-background-light">
                <h5 className="subtitle is-6">Calculated Metrics:</h5>
                <div className="columns is-multiline">
                  {submitResult.calculated_metrics?.pe_ratio && (
                    <div className="column is-4">
                      <strong>P/E Ratio:</strong> {submitResult.calculated_metrics.pe_ratio}
                    </div>
                  )}
                  {submitResult.calculated_metrics?.pb_ratio && (
                    <div className="column is-4">
                      <strong>P/B Ratio:</strong> {submitResult.calculated_metrics.pb_ratio}
                    </div>
                  )}
                  {submitResult.calculated_metrics?.net_profit_margin && (
                    <div className="column is-4">
                      <strong>NPM:</strong> {submitResult.calculated_metrics.net_profit_margin}%
                    </div>
                  )}
                  {submitResult.calculated_metrics?.roe && (
                    <div className="column is-4">
                      <strong>ROE:</strong> {submitResult.calculated_metrics.roe}%
                    </div>
                  )}
                  {submitResult.calculated_metrics?.roa && (
                    <div className="column is-4">
                      <strong>ROA:</strong> {submitResult.calculated_metrics.roa}%
                    </div>
                  )}
                  {submitResult.calculated_metrics?.debt_to_equity && (
                    <div className="column is-4">
                      <strong>D/E:</strong> {submitResult.calculated_metrics.debt_to_equity}
                    </div>
                  )}
                </div>
              </div>

              <div className="box has-background-info-light mt-3">
                <h5 className="subtitle is-6">Overall Signal:</h5>
                <p className="is-size-4 has-text-weight-bold">
                  {submitResult.signals?.overall_fundamental_signal?.toUpperCase().replace('_', ' ')}
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Metadata Section */}
              <div className="box">
                <h5 className="subtitle is-5 mb-4">Period Information</h5>
                <div className="columns is-multiline">
                  <div className="column is-3">
                    <div className="field">
                      <label className="label">Quarter *</label>
                      <div className="control">
                        <Controller
                          name="quarter"
                          control={control}
                          render={({ field }) => (
                            <div className="select is-fullwidth">
                              <select {...field}>
                                <option value="Q1">Q1 (Jan-Mar)</option>
                                <option value="Q2">Q2 (Apr-Jun)</option>
                                <option value="Q3">Q3 (Jul-Sep)</option>
                                <option value="Q4">Q4 (Oct-Dec)</option>
                              </select>
                            </div>
                          )}
                        />
                      </div>
                      {errors.quarter && (
                        <p className="help is-danger">{errors.quarter.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="column is-3">
                    <div className="field">
                      <label className="label">Year *</label>
                      <div className="control">
                        <Controller
                          name="year"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              className="input"
                              type="number"
                              placeholder="2024"
                            />
                          )}
                        />
                      </div>
                      {errors.year && (
                        <p className="help is-danger">{errors.year.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="column is-3">
                    <div className="field">
                      <label className="label">Quarter End Date *</label>
                      <div className="control">
                        <Controller
                          name="quarter_end_date"
                          control={control}
                          render={({ field }) => (
                            <input {...field} className="input" type="date" />
                          )}
                        />
                      </div>
                      {errors.quarter_end_date && (
                        <p className="help is-danger">{errors.quarter_end_date.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="column is-3">
                    <div className="field">
                      <label className="label">Financial Year *</label>
                      <div className="control">
                        <Controller
                          name="financial_year"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              className="input"
                              type="text"
                              placeholder="FY2024"
                            />
                          )}
                        />
                      </div>
                      {errors.financial_year && (
                        <p className="help is-danger">{errors.financial_year.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs for different sections */}
              <div className="tabs is-boxed">
                <ul>
                  <li className={activeTab === 'basic' ? 'is-active' : ''}>
                    <a onClick={() => setActiveTab('basic')}>
                      <span>Basic Metrics</span>
                    </a>
                  </li>
                  <li className={activeTab === 'profitability' ? 'is-active' : ''}>
                    <a onClick={() => setActiveTab('profitability')}>
                      <span>Profitability</span>
                    </a>
                  </li>
                  <li className={activeTab === 'valuation' ? 'is-active' : ''}>
                    <a onClick={() => setActiveTab('valuation')}>
                      <span>Valuation</span>
                    </a>
                  </li>
                  <li className={activeTab === 'balance' ? 'is-active' : ''}>
                    <a onClick={() => setActiveTab('balance')}>
                      <span>Balance Sheet</span>
                    </a>
                  </li>
                  <li className={activeTab === 'debt' ? 'is-active' : ''}>
                    <a onClick={() => setActiveTab('debt')}>
                      <span>Debt & Liquidity</span>
                    </a>
                  </li>
                  <li className={activeTab === 'cash' ? 'is-active' : ''}>
                    <a onClick={() => setActiveTab('cash')}>
                      <span>Cash Flow</span>
                    </a>
                  </li>
                </ul>
              </div>

              {/* Basic Metrics Tab */}
              {activeTab === 'basic' && (
                <div className="box">
                  <h5 className="subtitle is-5 mb-4">Revenue & Earnings (in Crores)</h5>
                  <div className="columns is-multiline">
                    <div className="column is-4">
                      <div className="field">
                        <label className="label">EPS (₹)</label>
                        <Controller
                          name="eps"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "45.50")}
                        />
                        {errors.eps && <p className="help is-danger">{errors.eps.message}</p>}
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">EPS Growth (%)</label>
                        <Controller
                          name="eps_growth"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                              className="input"
                              type="number"
                              step="0.01"
                              placeholder="15.5"
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Diluted EPS (₹)</label>
                        <Controller
                          name="diluted_eps"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                              className="input"
                              type="number"
                              step="0.01"
                              placeholder="44.80"
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Revenue (Cr)</label>
                        <Controller
                          name="revenue"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                              className="input"
                              type="number"
                              step="0.01"
                              placeholder="25000"
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Revenue Growth YoY (%)</label>
                        <Controller
                          name="revenue_growth_yoy"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                              className="input"
                              type="number"
                              step="0.01"
                              placeholder="15.5"
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Revenue Growth QoQ (%)</label>
                        <Controller
                          name="revenue_growth_qoq"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                              className="input"
                              type="number"
                              step="0.01"
                              placeholder="5.2"
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Net Profit (Cr)</label>
                        <Controller
                          name="net_profit"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                              className="input"
                              type="number"
                              step="0.01"
                              placeholder="3500"
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Order Book (Cr)</label>
                        <Controller
                          name="order_book"
                          control={control}
                          render={({ field }) => (
                            <input
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                              className="input"
                              type="number"
                              step="0.01"
                              placeholder="50000"
                            />
                          )}
                        />
                        <p className="help">For infrastructure/capital goods companies</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Profitability Tab */}
              {activeTab === 'profitability' && (
                <div className="box">
                  <h5 className="subtitle is-5 mb-4">Profitability Metrics (%)</h5>
                  <div className="columns is-multiline">
                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Net Profit Margin (%)</label>
                        <Controller
                          name="net_profit_margin"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "14.0")}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Operating Profit Margin (%)</label>
                        <Controller
                          name="operating_profit_margin"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "18.5")}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Gross Profit Margin (%)</label>
                        <Controller
                          name="gross_profit_margin"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "35.0")}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">ROE - Return on Equity (%)</label>
                        <Controller
                          name="roe"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "18.5")}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">ROCE - Return on Capital Employed (%)</label>
                        <Controller
                          name="roce"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "16.2")}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">ROA - Return on Assets (%)</label>
                        <Controller
                          name="roa"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "7.5")}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Asset Turnover Ratio</label>
                        <Controller
                          name="asset_turnover"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "1.2")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Valuation Tab */}
              {activeTab === 'valuation' && (
                <div className="box">
                  <h5 className="subtitle is-5 mb-4">Valuation Ratios</h5>
                  <div className="columns is-multiline">
                    <div className="column is-6">
                      <div className="field">
                        <label className="label">P/E Ratio</label>
                        <Controller
                          name="pe_ratio"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "24.5")}
                        />
                        <p className="help">Leave blank to auto-calculate from EPS</p>
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">P/B Ratio</label>
                        <Controller
                          name="pb_ratio"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "2.8")}
                        />
                        <p className="help">Leave blank to auto-calculate from Book Value</p>
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Price to Sales Ratio</label>
                        <Controller
                          name="price_to_sales"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "3.5")}
                        />
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">EV/EBITDA</label>
                        <Controller
                          name="ev_to_ebitda"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "15.2")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Balance Sheet Tab */}
              {activeTab === 'balance' && (
                <div className="box">
                  <h5 className="subtitle is-5 mb-4">Balance Sheet (in Crores)</h5>
                  <div className="columns is-multiline">
                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Total Assets (Cr)</label>
                        <Controller
                          name="total_assets"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "50000")}
                        />
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Total Liabilities (Cr)</label>
                        <Controller
                          name="total_liabilities"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "15000")}
                        />
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Shareholders Equity (Cr)</label>
                        <Controller
                          name="shareholders_equity"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "35000")}
                        />
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Book Value Per Share (₹)</label>
                        <Controller
                          name="book_value_per_share"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "875")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Debt & Liquidity Tab */}
              {activeTab === 'debt' && (
                <div className="box">
                  <h5 className="subtitle is-5 mb-4">Debt & Liquidity Metrics</h5>
                  <div className="columns is-multiline">
                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Debt to Equity Ratio</label>
                        <Controller
                          name="debt_to_equity"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "0.45")}
                        />
                        <p className="help">Leave blank to auto-calculate</p>
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Debt to Assets Ratio</label>
                        <Controller
                          name="debt_to_assets"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "0.30")}
                        />
                      </div>
                    </div>

                    <div className="column is-4">
                      <div className="field">
                        <label className="label">Interest Coverage Ratio</label>
                        <Controller
                          name="interest_coverage"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "8.5")}
                        />
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Current Ratio</label>
                        <Controller
                          name="current_ratio"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "1.5")}
                        />
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Quick Ratio</label>
                        <Controller
                          name="quick_ratio"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "1.2")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cash Flow Tab */}
              {activeTab === 'cash' && (
                <div className="box">
                  <h5 className="subtitle is-5 mb-4">Cash Flow (in Crores)</h5>
                  <div className="columns is-multiline">
                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Operating Cash Flow (Cr)</label>
                        <Controller
                          name="operating_cash_flow"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "4200")}
                        />
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Free Cash Flow (Cr)</label>
                        <Controller
                          name="free_cash_flow"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "2800")}
                        />
                      </div>
                    </div>

                    <div className="column is-6">
                      <div className="field">
                        <label className="label">Cash Flow Per Share (₹)</label>
                        <Controller
                          name="cash_flow_per_share"
                          control={control}
                          render={({ field }) => renderNumberInput(field, "105")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Data Source */}
              <div className="field">
                <label className="label">Data Source</label>
                <div className="control">
                  <Controller
                    name="data_source"
                    control={control}
                    render={({ field }) => (
                      <div className="select is-fullwidth">
                        <select {...field}>
                          <option value="Manual">Manual Entry</option>
                          <option value="BSE">BSE</option>
                          <option value="NSE">NSE</option>
                          <option value="Screener">Screener.in</option>
                          <option value="MoneyControl">MoneyControl</option>
                        </select>
                      </div>
                    )}
                  />
                </div>
              </div>

              <div className="notification is-info is-light mt-4">
                <p className="is-size-7">
                  <strong>Note:</strong> You only need to fill in the fields you have. The system will automatically:
                </p>
                <ul className="is-size-7 ml-4 mt-2">
                  <li>• Calculate missing ratios (P/E, P/B, ROE, ROA, D/E, etc.)</li>
                  <li>• Generate signals for each metric (bullish/bearish/neutral)</li>
                  <li>• Provide an overall fundamental signal (strong buy/buy/hold/sell/strong sell)</li>
                </ul>
              </div>
            </form>
          )}
        </section>

        {!submitResult && (
          <footer className="modal-card-foot">
            <button
              className={`button is-success ${loading ? "is-loading" : ""}`}
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
            >
              <span className="icon">
                <i className="fas fa-save"></i>
              </span>
              <span>Submit Financial Data</span>
            </button>
            <button className="button" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default FinancialDataModal;
